import { FC, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";

import axios from "axios";

import { AlertContainer } from "./AlertContainer";
import { AlertContext, AlertContextData, AlertData } from "./AlertContext";
import { AlertError, isAlertError } from "./AlertError";

/**
 * The root component for all alerts. Alerts triggered by children are displayed by the alert provider.
 */
export const AlertProvider: FC<PropsWithChildren<{}>> = ({ children }: PropsWithChildren<{}>): JSX.Element => {
  const [currentAlert, setCurrentAlert] = useState<AlertData | null>(null);

  const parentContext = useContext(AlertContext);

  const alertContextValue = useMemo<AlertContextData>(() => {
    return {
      default: false,
      showAlert: (alert: AlertData) => {
        // We reject a promise but don't block, so that the async rejection handler fires but the non-blocking behavior of showAlert is retained.
        new Promise((_, reject) => {
          const { message, type, variant, style, id, timeout } = alert;
          reject(new AlertError(message instanceof Error ? message.message : message, type, variant ?? style ?? "banner", id, timeout));
        });
      },
      hideAlert: () => {
        setCurrentAlert(null);
        parentContext?.hideAlert?.();
      },
    };
  }, [parentContext]);

  useEffect(() => {
    function errorHandler(ev: ErrorEvent) {
      const { error } = ev;

      if (isAlertError(error)) {
        setCurrentAlert({
          message: error.message,
          type: error.type,
          variant: error.variant ?? error.style ?? "banner",
          id: error.id,
          timeout: error.timeout,
        });
      } else if (axios.isAxiosError(error)) {
        if (process.env.NODE_ENV === "development") {
          console.error(`IMPORTANT
----------------
An unhandled networking error occurred, this should be handled appropriately with a try/catch block.
If you are using an asynchronous query (such as a mutation), it is recommended to instead await
the query and handle it via try/catch blocks instead of using the onError and onSuccess
callbacks.`);
        }
      } else if (error instanceof Error) {
        if (process.env.NODE_ENV === "development") {
          console.error(`IMPORTANT
----------------
An unhandled error occurred, this should be handled appropriately with a try/catch block. If
this is originating in an asynchronous call, make sure you are properly awaiting on the
asynchronous operation inside the try/catch.`);
        }

        setCurrentAlert({ message: error.message, type: "error", variant: "banner" });
      } else if (typeof error === "string") {
        setCurrentAlert({ message: error, type: "error", variant: "banner" });
      } else {
        setCurrentAlert({ message: "Unexpected error occurred", type: "error", variant: "banner" });
      }
      ev.stopPropagation();
      ev.preventDefault();
    }

    function rejectionHandler(ev: PromiseRejectionEvent) {
      const { reason } = ev;

      if (isAlertError(reason)) {
        setCurrentAlert({
          message: reason.message,
          type: reason.type,
          variant: reason.variant ?? reason.style ?? "banner",
          id: reason.id,
          timeout: reason.timeout,
        });
      } else if (axios.isAxiosError(reason)) {
        if (process.env.NODE_ENV === "development") {
          console.error(`IMPORTANT
----------------
An unhandled networking error occurred, this should be handled appropriately with a try/catch block.
If you are using an asynchronous query (such as a mutation), it is recommended to instead await
the query and handle it via try/catch blocks instead of using the onError and onSuccess
callbacks.`);
        }
      } else if (reason instanceof Error) {
        if (process.env.NODE_ENV === "development") {
          console.error(`IMPORTANT
----------------
An unhandled error occurred, this should be handled appropriately with a try/catch block. If
this is originating in an asynchronous call, make sure you are properly awaiting on the
asynchronous operation inside the try/catch.`);
        }

        setCurrentAlert({ message: reason.message, type: "error", variant: "banner" });
      } else if (typeof reason === "string") {
        setCurrentAlert({ message: reason, type: "error", variant: "banner" });
      } else {
        setCurrentAlert({ message: "Unexpected error occurred", type: "error", variant: "banner" });
      }
      ev.stopPropagation();
      ev.preventDefault();
    }

    window.addEventListener("error", errorHandler);
    window.addEventListener("unhandledrejection", rejectionHandler);

    return () => {
      window.removeEventListener("error", errorHandler);
      window.removeEventListener("unhandledrejection", rejectionHandler);
    };
  }, [alertContextValue]);

  return (
    <AlertContext.Provider value={alertContextValue}>
      <AlertContainer currentAlert={currentAlert} root={parentContext.default} />
      {children}
    </AlertContext.Provider>
  );
};
